import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Button, Alert, Table } from 'react-bootstrap';
import { useMetadataFields } from '../../../hooks/useMetadataFields';
import MetadataFieldTable from '../tables/MetadataFieldTable';
import { validateInput, validateObject } from '../../../utils/sanitizer';

/**
 * MetadataFieldsSection - Section component for managing metadata fields within a customer context
 * 
 * @param {string} customerId - The ID of the customer
 */
const MetadataFieldsSection = ({ customerId }) => {
  const {
    fields,
    loading,
    error: apiError,
    createField,
    updateField,
    deleteField,
    exportFields,
    downloadSampleJson,
    validateImportData,
    importFields
  } = useMetadataFields(customerId);

  const [showModal, setShowModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importedFields, setImportedFields] = useState([]);
  const [importLoading, setImportLoading] = useState(false);

  const [fieldForm, setFieldForm] = useState({
    name: '',
    fieldKey: '',
    dataType: 'string',
    description: ''
  });

  const handleCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (field) => {
    setEditingField(field);
    setFieldForm({
      name: field.name || '',
      fieldKey: field.fieldKey || '',
      dataType: field.dataType || 'string',
      description: field.description || ''
    });
    setShowModal(true);
  };

  const handleSaveField = async () => {
    // Validate input
    const validation = validateObject(fieldForm);
    if (!validation.isValid) {
      setError('Input contains potentially unsafe content.');
      return;
    }

    if (!fieldForm.name.trim() || !fieldForm.fieldKey.trim()) {
      setError('Field Name and Field Key are required.');
      return;
    }

    try {
      if (editingField) {
        await updateField(editingField.id, fieldForm);
        setSuccess('Metadata field updated successfully!');
      } else {
        await createField(fieldForm);
        setSuccess('Metadata field created successfully!');
      }

      setShowModal(false);
      resetForm();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save metadata field');
    }
  };

  const handleDelete = async (fieldId) => {
    if (window.confirm('Are you sure you want to delete this metadata field?')) {
      try {
        await deleteField(fieldId);
        setSuccess('Metadata field deleted successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError(err.message || 'Failed to delete metadata field');
      }
    }
  };

  const resetForm = () => {
    setEditingField(null);
    setFieldForm({
      name: '',
      fieldKey: '',
      dataType: 'string',
      description: ''
    });
    setError('');
  };

  const handleModalClose = () => {
    setShowModal(false);
    resetForm();
  };

  const handleImportConfigurations = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const configurations = JSON.parse(text);

      if (!Array.isArray(configurations)) {
        setError('Invalid JSON format. Expected an array of configurations.');
        setTimeout(() => setError(''), 5000);
        return;
      }

      const { fieldsToImport, invalidFields } = validateImportData(configurations);

      if (invalidFields.length > 0) {
        setError(`Import failed. Invalid fields found:\n${invalidFields.join('\n')}`);
        setTimeout(() => setError(''), 8000);
        return;
      }

      if (fieldsToImport.length === 0) {
        setError('No fields to import.');
        setTimeout(() => setError(''), 5000);
        return;
      }

      setImportedFields(fieldsToImport);
      setShowImportModal(true);
    } catch (err) {
      console.error('Import error:', err);
      setError(`Failed to parse configurations: ${err.message}`);
      setTimeout(() => setError(''), 5000);
    }

    // Reset file input
    event.target.value = '';
  };

  const handleSaveImportedFields = async () => {
    setImportLoading(true);
    try {
      const { importedCount, errors } = await importFields(importedFields);

      if (errors.length > 0) {
        setError(`Import completed with errors. ${importedCount} imported. Errors: ${errors.join(', ')}`);
      } else {
        setSuccess(`Import completed! ${importedCount} metadata fields imported successfully.`);
      }

      setShowImportModal(false);
      setImportedFields([]);
      setTimeout(() => { setSuccess(''); setError(''); }, 5000);
    } catch (err) {
      console.error('Save import error:', err);
      setError('Failed to save imported fields');
      setTimeout(() => setError(''), 5000);
    } finally {
      setImportLoading(false);
    }
  };

  return (
    <div>
      {(error || apiError) && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error || apiError}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}

      <MetadataFieldTable
        fields={fields}
        loading={loading}
        onCreate={handleCreate}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onExport={exportFields}
        onImport={handleImportConfigurations}
        onDownloadSample={downloadSampleJson}
      />

      {/* Create/Edit Metadata Field Modal */}
      <Modal show={showModal} onHide={handleModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingField ? 'Edit Metadata Field' : 'Add Metadata Field'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Field Name *</Form.Label>
              <Form.Control
                type="text"
                value={fieldForm.name}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!validateInput(value)) {
                    setError('Input contains potentially unsafe content.');
                    return;
                  }
                  setFieldForm({ ...fieldForm, name: value });
                }}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Field Key *</Form.Label>
              <Form.Control
                type="text"
                value={fieldForm.fieldKey}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!validateInput(value)) {
                    setError('Input contains potentially unsafe content.');
                    return;
                  }
                  setFieldForm({ ...fieldForm, fieldKey: value });
                }}
                required
              />
              <Form.Text className="text-muted">
                Use lowercase with underscores (e.g., custom_field_1)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Data Type</Form.Label>
              <Form.Select
                value={fieldForm.dataType}
                onChange={(e) => setFieldForm({ ...fieldForm, dataType: e.target.value })}
              >
                <option value="string">String</option>
                <option value="number">Number</option>
                <option value="date">Date</option>
                <option value="boolean">Boolean</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={fieldForm.description}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!validateInput(value)) {
                    setError('Input contains potentially unsafe content.');
                    return;
                  }
                  setFieldForm({ ...fieldForm, description: value });
                }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleModalClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveField}
            disabled={!fieldForm.name.trim() || !fieldForm.fieldKey.trim()}
          >
            {editingField ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Import Preview Modal */}
      <Modal show={showImportModal} onHide={() => setShowImportModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Import Preview - {importedFields.length} Metadata Fields</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '500px', overflowY: 'auto' }}>
          <Table striped bordered hover size="sm">
            <thead>
              <tr>
                <th>Action</th>
                <th>Field Name</th>
                <th>Field Key</th>
                <th>Data Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {importedFields.map((field, index) => (
                <tr key={index}>
                  <td>
                    <span className={`badge ${field.isUpdate ? 'bg-warning' : 'bg-success'}`}>
                      {field.isUpdate ? 'Update' : 'Create'}
                    </span>
                  </td>
                  <td>{field.name}</td>
                  <td><code>{field.fieldKey}</code></td>
                  <td>{field.dataType}</td>
                  <td className="text-truncate" style={{ maxWidth: '300px' }}>
                    {field.description || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImportModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveImportedFields}
            disabled={importLoading}
          >
            {importLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Creating Fields...
              </>
            ) : (
              `Save ${importedFields.length} Fields`
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

MetadataFieldsSection.propTypes = {
  customerId: PropTypes.string.isRequired
};

export default MetadataFieldsSection;
